/**
 * FullDocumentViewer
 *
 * Full-screen document viewer that takes over the entire modal.
 * Includes a back button to return to the main review view.
 */
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { formatDistanceToNow } from 'date-fns';
import { ArrowLeft, ChevronLeft, ChevronRight, FileEdit } from 'lucide-react';
import { useEffect, useMemo } from 'react';

import { AudioConversationPreview } from '@/components/audio-conversation-preview';
import { ConversationViewer } from '@/components/conversation-viewer';
import { BlobPreview } from '@/components/documents-v2/components/inspector/BlobPreview';
import { Button } from '@/components/ui/button';
import {
  useGetArtifactUnpackedQuery,
  useListArtifactsQuery,
} from '@/services/api/artifactService';
import { ArtifactType } from '@/services/types/artifact-digest-types';
import type { ArtifactUnpacked } from '@/services/types/artifact-types';

/** Get display name for artifact (same logic as Document Library) */
const getDisplayName = (artifact: ArtifactUnpacked | undefined): string => {
  if (!artifact) return 'Document';

  // 1) Blob: prefer uploaded file name
  if (artifact.artifact_type === ArtifactType.BLOB && artifact.blob?.name) {
    return artifact.blob.name;
  }

  // 2) Prefer digest title
  if (artifact.artifact_digest?.title) {
    return artifact.artifact_digest.title;
  }

  // 3) One-line summary
  if (artifact.artifact_digest?.one_line_summary) {
    return artifact.artifact_digest.one_line_summary;
  }

  // 4) Fallback by type
  switch (artifact.artifact_type) {
    case ArtifactType.TRANSCRIPT:
      return 'Transcript';
    case ArtifactType.AUDIO_CONVERSATION:
      return 'Audio Conversation';
    case ArtifactType.CONVERSATION:
      return 'Conversation';
    case ArtifactType.STAFF_NOTE:
      return 'Staff Note';
    case ArtifactType.BLOB:
    default:
      return 'Document';
  }
};

interface FullDocumentViewerProps {
  artifactId: string;
  matterId: string;
  onBack: () => void;
  onNavigate?: (artifactId: string) => void;
}

export const FullDocumentViewer = ({
  artifactId,
  matterId,
  onBack,
  onNavigate,
}: FullDocumentViewerProps) => {

  // Fetch the unpacked artifact data
  const { data: unpackedArtifact, isLoading: isLoadingArtifact } =
    useGetArtifactUnpackedQuery(
      {
        matterId: matterId || '',
        artifactId,
      },
      {
        skip: !artifactId || !matterId,
      },
    );

  // Fetch all artifacts for navigation
  const { data: artifacts = [] } = useListArtifactsQuery(
    { matterId: matterId || '' },
    { skip: !matterId },
  );

  // Compute navigation state (same sort order as Document Library)
  const sortedArtifactIds = useMemo(() => {
    return [...artifacts]
      .sort(
        (a, b) =>
          new Date(b.modified_at).getTime() - new Date(a.modified_at).getTime(),
      )
      .map((a) => a.id);
  }, [artifacts]);

  const currentIndex = sortedArtifactIds.indexOf(artifactId);
  const prevId = currentIndex > 0 ? sortedArtifactIds[currentIndex - 1] : null;
  const nextId =
    currentIndex < sortedArtifactIds.length - 1
      ? sortedArtifactIds[currentIndex + 1]
      : null;

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't navigate if user is typing in an input
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement
      ) {
        return;
      }

      if (e.key === 'ArrowLeft' && prevId && onNavigate) {
        onNavigate(prevId);
      }
      if (e.key === 'ArrowRight' && nextId && onNavigate) {
        onNavigate(nextId);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [prevId, nextId, onNavigate]);

  const renderPreviewContent = () => {
    if (isLoadingArtifact) {
      return (
        <div className="flex h-full items-center justify-center">
          <div className="flex flex-col items-center gap-2">
            <ArrowPathIcon className="h-8 w-8 animate-spin text-zinc-400 dark:text-zinc-500" />
            <p className="text-sm font-medium text-zinc-600 dark:text-zinc-400">
              Loading document...
            </p>
          </div>
        </div>
      );
    }

    if (!unpackedArtifact) {
      return (
        <div className="flex h-full items-center justify-center">
          <p className="text-sm font-medium text-zinc-600 dark:text-zinc-400">
            No preview available
          </p>
        </div>
      );
    }

    if (
      unpackedArtifact.artifact_type === ArtifactType.BLOB &&
      unpackedArtifact.blob
    ) {
      return <BlobPreview file={unpackedArtifact.blob} />;
    }

    if (unpackedArtifact.artifact_type === ArtifactType.AUDIO_CONVERSATION) {
      return <AudioConversationPreview artifact={unpackedArtifact} />;
    }

    if (unpackedArtifact.artifact_type === ArtifactType.TRANSCRIPT) {
      return <AudioConversationPreview artifact={unpackedArtifact} />;
    }

    if (unpackedArtifact.artifact_type === ArtifactType.CONVERSATION) {
      return <ConversationViewer artifact={unpackedArtifact} />;
    }

    if (
      unpackedArtifact.artifact_type === ArtifactType.STAFF_NOTE &&
      unpackedArtifact.staff_note
    ) {
      const staffNote = unpackedArtifact.staff_note;
      const authorName = staffNote.user
        ? `${staffNote.user.first_name} ${staffNote.user.last_name}`
        : 'Unknown';
      const createdAt = staffNote.created_at
        ? formatDistanceToNow(new Date(staffNote.created_at), {
            addSuffix: true,
          })
        : '';

      return (
        <div className="h-full overflow-y-auto">
          <div className="mx-auto max-w-3xl p-6">
            {/* Header */}
            <div className="mb-6 flex items-start gap-3">
              <div className="rounded-lg bg-indigo-100 dark:bg-indigo-900/30 p-2">
                <FileEdit className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-zinc-900 dark:text-white">
                  Staff Note
                </h2>
                <p className="text-sm text-zinc-600 dark:text-zinc-400">
                  By {authorName} {createdAt && `• ${createdAt}`}
                </p>
              </div>
            </div>

            {/* Content */}
            <div className="rounded-lg border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-4">
              <div className="prose prose-sm dark:prose-invert max-w-none whitespace-pre-wrap text-zinc-800 dark:text-zinc-200">
                {staffNote.content}
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="flex h-full items-center justify-center">
        <p className="text-sm font-medium text-zinc-600 dark:text-zinc-400">
          Preview not available for this artifact type
        </p>
      </div>
    );
  };

  const documentTitle = getDisplayName(unpackedArtifact);

  return (
    <div className="flex h-full flex-col bg-white dark:bg-zinc-900">
      {/* Header with back button, title, and navigation */}
      <div className="flex-shrink-0 border-b border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 px-4 py-3">
        <div className="flex items-center justify-between gap-4">
          {/* Left: Back button */}
          <Button
            variant="ghost"
            size="sm"
            onClick={onBack}
            className="h-8 gap-1.5 px-2 text-sm font-medium text-zinc-700 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800 flex-shrink-0"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Back to Review
          </Button>

          {/* Center: Document title */}
          <h2 className="text-sm font-medium text-zinc-900 dark:text-white truncate min-w-0">
            {documentTitle}
          </h2>

          {/* Right: Navigation controls */}
          {sortedArtifactIds.length > 1 && onNavigate ? (
            <div className="flex items-center gap-2 flex-shrink-0">
              <span className="text-xs font-medium text-zinc-500 dark:text-zinc-400">
                {currentIndex + 1} of {sortedArtifactIds.length}
              </span>
              <Button
                variant="ghost"
                size="sm"
                disabled={!prevId}
                onClick={() => prevId && onNavigate(prevId)}
                className="h-7 w-7 p-0 disabled:opacity-50"
              >
                <ChevronLeft className="h-3.5 w-3.5" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                disabled={!nextId}
                onClick={() => nextId && onNavigate(nextId)}
                className="h-7 w-7 p-0 disabled:opacity-50"
              >
                <ChevronRight className="h-3.5 w-3.5" />
              </Button>
            </div>
          ) : (
            <div className="w-[120px] flex-shrink-0" /> /* Spacer for alignment */
          )}
        </div>
      </div>

      {/* Document viewer */}
      <div className="flex-1 overflow-hidden">{renderPreviewContent()}</div>
    </div>
  );
};
